import React, { useEffect, useState } from "react";
import AddIcon from "@material-ui/icons/Add";
import clsx from "clsx";
import { DiagramEngine } from "@projectstorm/react-diagrams";
import { useNavigate } from "react-router-dom";
import Port from "./Port";
import Group from "./Group";
import { getPortName, getGroupPortName } from "../../../utils/methods";
import { NENodeModel } from "./CustomNode/NENodeModel";
import AuthService from "../../../services/auth.service";
import { NODE_CONSTANTS } from "../../../utils/appConstants";
import { PORT_GROUP_SIZE, PORT_SIZE_WITH_MARGIN, PORT_SIZE, BANDWIDTH_SIZE, getPortGrid, PORT_SIZE_ON_GRID } from "./DraggableUtil";

interface NENodeProps {
  engine: DiagramEngine;
  node: NENodeModel;
  onGroupExpandCollapse?: (e: any) => void;
  onRefetch: any;
  nodeHeight: number;
  nodeWidth: number;
  refreshPorts: boolean;
}

const NENode: React.FC<NENodeProps> = ({ engine, node, onRefetch, onGroupExpandCollapse, nodeHeight, nodeWidth, refreshPorts }) => {
  const { groups: portGroups, selectedNe, portsUsed: bandwidthPorts, groupsUsed: bandwidthGroups, portsList: ports } = node;
  const [componentRendered, setComponentRendered] = React.useState(false);
  const [isDragging, setIsDragging] = useState(false);
  let dragTimeout: NodeJS.Timeout;
  useEffect(() => {
    setComponentRendered(true);
  }, []);
  const { management_ip, on_net, ne_name } = selectedNe; // TODO: should we expose management_ip?
  const outerRef = React.useRef<HTMLDivElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const updateCoords = React.useCallback(
    (port: any) => {
      const name = getPortName(port.portId);
      const portDetails = node.getPort(name);
      portDetails && portDetails.updateCoords(engine.getPortCoords(portDetails));
    },
    [node, engine]
  );

  const handleDragEnd = () => {
    if (isDragging) {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
    }
    dragTimeout = setTimeout(() => {
      const event = new CustomEvent("nodeDragComplete", {
        detail: { nodeId: selectedNe.ne_name },
      });
      window.dispatchEvent(event);
      setIsDragging(false);
    }, 10);
  };

  // When height/width changes, update port position with the ratio of the difference.
  const [lastWidth, setLastWidth] = useState<number>(nodeWidth);
  const [lastHeight, setLastHeight] = useState<number>(nodeHeight);
  React.useLayoutEffect(() => {
    if (engine && engine.getModel()) {
      const widthMargin = PORT_SIZE_WITH_MARGIN - PORT_SIZE.width;
      const heightMargin = PORT_SIZE_WITH_MARGIN - PORT_SIZE.height;

      
      const adjustedWidthPort = nodeWidth - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_SIZE.width + widthMargin );
      const adjustedLastWidthPort = lastWidth - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_SIZE.width + widthMargin );
      const adjustedHeightPort = nodeHeight - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_SIZE.height + heightMargin );
      const adjustedLastHeightPort = lastHeight - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_SIZE.height + heightMargin );
      const xScalePort = adjustedWidthPort / adjustedLastWidthPort;
      const yScalePort = adjustedHeightPort / adjustedLastHeightPort;

      const adjustedWidthGroup = nodeWidth - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_GROUP_SIZE.width + widthMargin );
      const adjustedLastWidthGroup = lastWidth - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_GROUP_SIZE.width + widthMargin );
      const adjustedHeightGroup = nodeHeight - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_GROUP_SIZE.height + heightMargin );
      const adjustedLastHeightGroup = lastHeight - (NODE_CONSTANTS.PERIMETER_THICKNESS * 2) - (PORT_GROUP_SIZE.height + heightMargin );
      const xScaleGroup = adjustedWidthGroup / adjustedLastWidthGroup;
      const yScaleGroup = adjustedHeightGroup / adjustedLastHeightGroup;

      const adjustedWidthBandwidthPort = nodeWidth - (BANDWIDTH_SIZE.width + widthMargin );
      const adjustedLastWidthBandwidthPort = lastWidth - (BANDWIDTH_SIZE.width + widthMargin );
      const adjustedHeightBandwidthPort = nodeHeight - (BANDWIDTH_SIZE.height + heightMargin );
      const adjustedLastHeightBandwidthPort = lastHeight - (BANDWIDTH_SIZE.height + heightMargin );
      const xScaleBandwidthPort = adjustedWidthBandwidthPort / adjustedLastWidthBandwidthPort;
      const yScaleBandwidthPort = adjustedHeightBandwidthPort / adjustedLastHeightBandwidthPort;
      
      const adjustedWidthBandwidthGroup = nodeWidth - (BANDWIDTH_SIZE.width + widthMargin );
      const adjustedLastWidthBandwidthGroup = lastWidth - (BANDWIDTH_SIZE.width + widthMargin );
      const adjustedHeightBandwidthGroup = nodeHeight - (BANDWIDTH_SIZE.height + heightMargin );
      const adjustedLastHeightBandwidthGroup = lastHeight - (BANDWIDTH_SIZE.height + heightMargin );
      const xScaleBandwidthGroup = adjustedWidthBandwidthGroup / adjustedLastWidthBandwidthGroup;
      const yScaleBandwidthGroup = adjustedHeightBandwidthGroup / adjustedLastHeightBandwidthGroup;

      const allPorts = [];
      if (bandwidthGroups) allPorts.push(...bandwidthGroups);
      if (bandwidthPorts) allPorts.push(...bandwidthPorts);
      if (portGroups) allPorts.push(...portGroups);
      if (ports) allPorts.push(...ports);
      
      allPorts.forEach((port: any) => {
        // console.log("port.x", port.x);

        // Determine the scaling factor based on the type of element
        if (bandwidthGroups && bandwidthGroups.includes(port)) {
          // Apply scaling for bandwidthGroups
          port.x *= xScaleBandwidthGroup;
          port.y *= yScaleBandwidthGroup;
        } else if (bandwidthPorts && bandwidthPorts.includes(port)) {
          // Apply scaling for bandwidthPorts
          port.x *= xScaleBandwidthPort;
          port.y *= yScaleBandwidthPort;
        } else if (portGroups && portGroups.includes(port)) {
          // Apply scaling for portGroups
          port.x *= xScaleGroup;
          port.y *= yScaleGroup;
        } else {
          // Apply scaling for ports
          port.x *= xScalePort;
          port.y *= yScalePort;
        }

        // console.log("scaled port.x", port.x);
        updateCoords(port);
    });
}
    setLastWidth(nodeWidth);
    setLastHeight(nodeHeight);
  }, [nodeHeight, nodeWidth]);

  React.useLayoutEffect(() => {
    if (engine && engine.getModel()) {
      const allPorts = [];
      if (ports) allPorts.push(...ports);
      if (portGroups) allPorts.push(...portGroups);
      if (bandwidthPorts) allPorts.push(...bandwidthPorts);
      if (bandwidthGroups) allPorts.push(...bandwidthGroups);
      allPorts.forEach((port: any) => {
        updateCoords(port);
      });
    }
  }, [refreshPorts]);
  const linkComponents = React.useMemo(() => {
    const result = [];
    result.push(
      bandwidthPorts.map((port: any, index: number) => {
        const name = getPortName(port.portId);
        const portDetails = node.getPort(name);
        const key = `r-${index}`;
        if (!outerRef?.current) return null;
        return <Port key={key} port={port} engine={engine} portModel={portDetails} isBandwidth boundsRef={outerRef.current} />;
      })
    );
    result.push(
      bandwidthGroups.map((group: any, index: number) => {
        if (!outerRef?.current) return null;
        return (
          <Group
            refresh={refreshPorts}
            engine={engine}
            node={node}
            group={group}
            key={index}
            onGroupExpandCollapse={onGroupExpandCollapse}
            isBandwidth
            boundsRef={outerRef.current}
          />
        );
      })
    );
    return result;
  }, [
    bandwidthPorts.right,
    bandwidthPorts.left,
    bandwidthGroups.right,
    bandwidthGroups.left,
    engine,
    node,
    componentRendered,
    nodeHeight,
    nodeWidth,
    lastHeight,
    lastWidth,
    refreshPorts,
  ]);

  type boxType = { x: any; y: any; width: any; height: any };
  const isCollided = React.useCallback((object1: boxType, object2: boxType) => {
    return (
      object1.x + object1.width > object2.x &&
      object2.x + object2.width > object1.x &&
      object1.y + object1.height > object2.y &&
      object2.y + object2.height > object1.y
    );
  }, []);

  const doCollisionCheck = false;
  const portComponents = React.useMemo(() => {
    const portList = [];
    const collisionList: boxType[] = [];
    const collidedGroupsList = [];
    const collidedPortsList: { x: any; y: any; width: any; height: any }[] = [];
    if ((!ports || ports.length <= 0) && (!portGroups || portGroups.length <= 0)) {
      return (
        <div className="no-ports" onClick={() => navigate("/add/port", { state: selectedNe })} data-testid="no-ports">
          <div className="text">
            <label>Add an Existing Network Resource</label>
          </div>
          <div className="plus-sign">
            <AddIcon />
          </div>
        </div>
      );
    } else {
      if (portGroups)
        portList.push(
          portGroups.map((group: any, index: number) => {
            const collisionObject = { x: group.x, y: group.y, ...PORT_GROUP_SIZE };
            if (!innerRef?.current) return;
            const collision = collisionList.find((ele) => isCollided(ele, collisionObject));
            if (collision && doCollisionCheck) {
              collidedGroupsList.push(collision);
            } else {
              collisionList.push(collisionObject);
              return (
                <Group
                  refresh={refreshPorts}
                  engine={engine}
                  node={node}
                  group={group}
                  key={index}
                  onGroupExpandCollapse={onGroupExpandCollapse}
                  boundsRef={innerRef.current}
                />
              );
            }
          })
        );
      if (ports)
        portList.push(
          ports
            .filter((port: any) => !(port.status === "port_put_up_for_sale" && port.organizationId != localStorage.getItem("org_id")))
            .map((port: any, index: number) => {
              const collisionObject = { x: port.x, y: port.y, ...PORT_SIZE };
              const collision = collisionList.find((ele) => isCollided(ele, collisionObject));
              if (collision && doCollisionCheck) {
                collidedPortsList.push(collision);
              } else {
                collisionList.push(collisionObject);
                const name = getPortName(port.portId);
                const portDetails = node.getPort(name);
                if (!innerRef?.current) return;
                return <Port boundsRef={innerRef.current} key={index} port={port} engine={engine} portModel={portDetails} />;
              }
            })
        );

      //   const portGrid = getPortGrid(nodeHeight, nodeWidth);
      //   let currentPosition = 0;
      //   let i;
      //   for (i = 0; i < collidedPortsList.length; i++) {
      //     const found = collisionList.find(
      //       (ele) =>
      //         !isCollided(ele, {
      //           x: (currentPosition % portGrid.x) * PORT_SIZE_ON_GRID,
      //           y: Math.floor(currentPosition++ / portGrid.x) * PORT_SIZE_ON_GRID,
      //           ...PORT_SIZE,
      //         })
      //     );
      //     if (found) {

      //     }
      //   }
    }
    return portList;
  }, [ports, portGroups, engine, node, componentRendered, nodeHeight, nodeWidth, lastHeight, lastWidth, refreshPorts]);

  const mesurements = React.useMemo(() => {
    const edgeLength = NODE_CONSTANTS.PERIMETER_THICKNESS;
    return {
      width: nodeWidth - edgeLength * 2,
      height: nodeHeight - edgeLength * 2,
      top: edgeLength,
      left: edgeLength,
    };
  }, [nodeWidth, nodeHeight]);

  return (
    <div
      onMouseLeave={handleDragEnd}
      onMouseOver={handleDragEnd}
      className="outside-ring"
      ref={outerRef}
      style={{ width: nodeWidth, height: nodeHeight, position: "relative" }}
      onMouseDown={(e: any) => {
        try {
          const classArray: string[] = [...e.target.classList];
          if (!classArray.some((ele: string) => ele === "allow-propagation")) throw "allow-propagation not found";
        } catch {
          e.stopPropagation();
        }
      }}
    >
      {linkComponents}
      <div ref={innerRef} className="inside-ring" style={{ position: "absolute", ...mesurements }}>
        {portComponents}
      </div>
    </div>
  );
};

export default NENode;
